import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { ArrowRight, Quote } from "lucide-react";

const BANNER = "/origini-hero.jpg";
const PORTRAIT = "/origini-camillo-cartoon.jpg";
const VESUVIO = "/origini-gaetano.jpg";

const LeOrigini = () => {
  const { t } = useTranslation();
  return (
    <PageShell>
      <SeoHead pageKey="origini" image={BANNER} />
      <PageBanner
        eyebrow={t("origini.banner.eyebrow")}
        title={t("origini.banner.title")}
        subtitle={t("origini.banner.subtitle")}
        image={BANNER}
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
            {t("origini.introEyebrow")}
          </div>
          <p
            className="text-lg sm:text-xl leading-relaxed"
            style={{ color: "#3b2417" }}
            dangerouslySetInnerHTML={{ __html: t("origini.intro") }}
          />
        </div>
      </section>

      <section className="section-y" style={{ background: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img src={PORTRAIT} alt={t("origini.portraitAlt")} className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(42,22,12,0.6) 100%)" }} />
            </div>
            <div
              className="absolute -top-6 -left-6 w-28 h-28 rounded-full flex items-center justify-center font-serif-display font-bold text-2xl shadow-xl"
              style={{ background: "#c5371f", color: "#fff" }}
            >
              1934
            </div>
            <div
              className="absolute -bottom-6 -right-6 rounded-2xl px-5 py-4 shadow-xl max-w-xs"
              style={{ background: "#2a160c", color: "#f5ead6" }}
            >
              <Quote size={16} style={{ color: "#c5371f" }} />
              <p className="mt-1 text-sm italic">{t("origini.quote")}</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>
              {t("origini.portraitEyebrow")}
            </span>
            <h2
              className="font-serif-display font-bold mt-3 leading-tight"
              style={{ color: "#2a160c", fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}
            >
              {t("origini.portraitTitle")}
            </h2>
            <div className="mt-6 space-y-5 text-[15px] sm:text-base leading-relaxed" style={{ color: "#3b2417" }}>
              <p dangerouslySetInnerHTML={{ __html: t("origini.portraitP1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("origini.portraitP2") }} />
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { n: "1934", l: t("origini.stat1") },
                { n: "1960", l: t("origini.stat2") },
                { n: t("origini.stat3Value"), l: t("origini.stat3") },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-4 text-center" style={{ background: "#fbf5e9", border: "1px solid rgba(91,54,33,0.15)" }}>
                  <div className="font-serif-display text-2xl sm:text-3xl font-bold" style={{ color: "#c5371f" }}>{s.n}</div>
                  <div className="text-[11px] uppercase tracking-wider mt-1" style={{ color: "#5b3621" }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-y relative overflow-hidden" style={{ background: "#2a160c", color: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>
              {t("origini.continueEyebrow")}
            </span>
            <h2 className="font-serif-display font-bold mt-3 leading-tight" style={{ fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}>
              {t("origini.continueTitle")}
            </h2>
            <p className="mt-6 leading-relaxed" style={{ color: "#ecdcbd" }} dangerouslySetInnerHTML={{ __html: t("origini.continueP1") }} />
            <p className="mt-4 leading-relaxed" style={{ color: "#ecdcbd" }} dangerouslySetInnerHTML={{ __html: t("origini.continueP2") }} />
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/nel-tempo" className="btn-red focus-ring inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold uppercase tracking-wider text-sm">
                {t("origini.ctaTempo")} <ArrowRight size={14} />
              </Link>
              <Link to="/zio-camillo" className="btn-outline-cream focus-ring inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold uppercase tracking-wider text-sm">
                {t("origini.ctaCamillo")}
              </Link>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={VESUVIO}
                alt={t("origini.vesuvioAlt")}
                className="w-full h-[520px] object-cover"
                style={{ objectPosition: "center 25%" }}
              />
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default LeOrigini;
